import { ComponentType, useState, useEffect } from 'react';

export interface PluginManifest {
  id: string;
  name: string;
  version: string;
  description: string;
  icon: string;
  route: string;
  component: string;
  permissions: string[];
  dependencies: string[];
  scope?: 'diagnostic-core' | 'workspace' | 'system' | 'user';
}

export interface LoadedPlugin {
  manifest: PluginManifest;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: ComponentType<any>;
  isEnabled: boolean;
}

export interface PluginRegistry {
  allPlugins: LoadedPlugin[];
  enabledPlugins: LoadedPlugin[];
  getPlugin: (id: string) => LoadedPlugin | undefined;
  isPluginEnabled: (id: string) => boolean;
  enablePlugin: (id: string) => void;
  disablePlugin: (id: string) => void;
}

class PluginRegistryImpl implements PluginRegistry {
  private plugins: Map<string, LoadedPlugin> = new Map();
  private enabledPluginIds: Set<string> = new Set();

  constructor() {
    this.loadPlugins().catch(error => {
      console.error('Failed to initialize plugin registry:', error);
    });
  }

  private async loadPlugins() {
    try {
      const pluginModules = await Promise.allSettled([
        import('../plugins/hivectl-bridge/index.js'),
        import('../plugins/settings-plugin/index'),
        import('../plugins/arcGit/index'),
        import('../plugins/ai-scout/index'),
        import('../plugins/designToCode/index'),
        import('../plugins/etl-wizard/index'),
        import('../plugins/ABDA/index'),
        import('../components/Beehive/SoftwareQualityPanel')
      ]);

      // Extract successful modules and log failures
      const [
        hivectlResult,
        settingsResult,
        arcGitResult,
        aiScoutResult,
        designToCodeResult,
        etlWizardResult,
        abdaResult,
        softwareQualityResult
      ] = pluginModules;

      // Check for failed imports and log them
      pluginModules.forEach((result, index) => {
        if (result.status === 'rejected') {
          console.error(`Plugin ${index} failed to load:`, result.reason);
        }
      });

      // Only proceed with successful imports
      if (hivectlResult.status === 'fulfilled' && 
          settingsResult.status === 'fulfilled' && 
          arcGitResult.status === 'fulfilled' && 
          aiScoutResult.status === 'fulfilled' && 
          designToCodeResult.status === 'fulfilled' && 
          etlWizardResult.status === 'fulfilled' && 
          abdaResult.status === 'fulfilled' && 
          softwareQualityResult.status === 'fulfilled') {
        
        const [HivectlBridgePlugin, SettingsPlugin, ArcGitPlugin, AIScoutPlugin, DesignToCodePlugin, ETLWizardPlugin, ABDAPlugin, SoftwareQualityPanel] = [
          hivectlResult.value,
          settingsResult.value,
          arcGitResult.value,
          aiScoutResult.value,
          designToCodeResult.value,
          etlWizardResult.value,
          abdaResult.value,
          softwareQualityResult.value
        ];
        const settingsPluginData: LoadedPlugin = {
          manifest: {
            id: 'settings-plugin',
            name: 'Settings',
            version: '1.0.0',
            description: 'Comprehensive settings management plugin',
            icon: 'Settings',
            route: '/settings',
            component: 'SettingsPlugin',
            permissions: ['settings:read', 'settings:write'],
            dependencies: ['settingsService']
          },
          component: SettingsPlugin.default || SettingsPlugin.SettingsPlugin,
          isEnabled: true
        };

        const arcGitPluginData: LoadedPlugin = {
          manifest: {
            id: 'arcgit-plugin',
            name: 'Source Control',
            version: '1.0.0',
            description: 'Git repository management and source control operations',
            icon: 'GitBranch',
            route: '/source-control',
            component: 'ArcGitPlugin',
            permissions: ['git:read', 'git:write', 'repo:clone', 'repo:branch', 'repo:commit'],
            dependencies: ['gitService', 'repoStatsService']
          },
          component: ArcGitPlugin.default || ArcGitPlugin.ArcGitPlugin,
          isEnabled: true
        };

        const aiScoutPluginData: LoadedPlugin = {
          manifest: {
            id: 'ai-scout',
            name: 'AI Scout',
            version: '1.0.0',
            description: 'Competitive intelligence system with trend analysis and gap detection',
            icon: 'Search',
            route: '/ai-scout',
            component: 'AIScoutPlugin',
            permissions: ['insights:read', 'competitors:read', 'trends:read'],
            dependencies: ['insightsService', 'competitorService']
          },
          component: AIScoutPlugin.AIScoutPlugin,
          isEnabled: true
        };

        const designToCodePluginData: LoadedPlugin = {
          manifest: {
            id: 'design-to-code-plugin',
            name: 'Design→Code',
            version: '1.0.0',
            description: 'Convert design files (Figma, Sketch, XD) to React/Vue code automatically',
            icon: 'Code',
            route: '/design-to-code',
            component: 'DesignToCodePlugin',
            permissions: ['file:upload', 'code:generate', 'storage:read', 'storage:write'],
            dependencies: ['designToCodeBee', 'storageService']
          },
          component: DesignToCodePlugin.default || DesignToCodePlugin.DesignToCodePlugin,
          isEnabled: false
        };

        // Keep only frontend-safe plugins here; server-side plugins are disabled for Netlify build
        // const aiScoutPluginSrcData: LoadedPlugin = { ... }
        // const accuracyTrackerPluginData: LoadedPlugin = { ... }
        // const alertPluginData: LoadedPlugin = { ... }
        // const memoryHydratorPluginData: LoadedPlugin = { ... }
        // const indexSearchPluginData: LoadedPlugin = { ... }

        const hivectlBridgePluginData: LoadedPlugin = {
          manifest: {
            id: 'hivectl-bridge-plugin',
            name: 'HiveCtl Bridge',
            version: '1.0.0',
            description: 'Bridge interface for hivectl CLI operations and system control',
            icon: 'Terminal',
            route: '/hivectl-bridge',
            component: 'HivectlBridgePlugin',
            permissions: ['cli:read', 'system:monitor', 'arcos:control'],
            dependencies: ['hivectlService', 'arcosOrchestrator']
          },
          component: (HivectlBridgePlugin.default || HivectlBridgePlugin) as ComponentType<any>,
          isEnabled: true
        };

        const softwareQualityPluginData: LoadedPlugin = {
          manifest: {
            id: 'software-quality-plugin',
            name: 'Software Quality',
            version: '1.0.0',
            description: 'Review code quality metrics and analyses',
            icon: 'BarChart2',
            route: '/software-quality',
            component: 'SoftwareQualityPanel',
            permissions: ['quality:read'],
            dependencies: []
          },
          component: (SoftwareQualityPanel as any).default || (SoftwareQualityPanel as any).SoftwareQualityPanel,
          isEnabled: true
        };

        const abdaPluginData: LoadedPlugin = {
          manifest: {
            id: 'abda-browser',
            name: 'Browser',
            version: '1.0.0',
            description: 'Automated Browser Data Acquisition - unified browser viewport with session management',
            icon: 'Globe',
            route: '/browser',
            component: 'ABDAViewport',
            permissions: ['abda:read', 'abda:write', 'abda:navigate'],
            dependencies: ['abdaService', 'sessionManager']
          },
          component: ABDAPlugin.ABDAViewport,
          isEnabled: true
        };

        [
          settingsPluginData,
          arcGitPluginData,
          aiScoutPluginData,
          designToCodePluginData,
          hivectlBridgePluginData,
          softwareQualityPluginData,
          abdaPluginData
        ].forEach(plugin => {
          this.plugins.set(plugin.manifest.id, plugin);
          this.enabledPluginIds.add(plugin.manifest.id);
        });

        console.log('Plugin registry initialized with plugins:', Array.from(this.plugins.keys()));
      } else {
        console.warn('Some plugins failed to load, continuing with available plugins');
      }
    } catch (error) {
      console.error('Critical error in plugin registry initialization:', error);
      // Initialize with empty registry to prevent complete failure
      this.plugins = new Map();
      this.enabledPluginIds = new Set();
    }
  }

  get allPlugins(): LoadedPlugin[] {
    return Array.from(this.plugins.values());
  }

  get enabledPlugins(): LoadedPlugin[] {
    return this.allPlugins.filter(plugin => this.enabledPluginIds.has(plugin.manifest.id));
  }

  getPlugin(id: string): LoadedPlugin | undefined {
    return this.plugins.get(id);
  }

  isPluginEnabled(id: string): boolean {
    return this.enabledPluginIds.has(id);
  }

  enablePlugin(id: string): void {
    this.enabledPluginIds.add(id);
  }

  disablePlugin(id: string): void {
    this.enabledPluginIds.delete(id);
  }
}

let singleton: PluginRegistryImpl | null = null;
export function usePluginRegistry(): PluginRegistry {
  const [registry, setRegistry] = useState<PluginRegistryImpl | null>(singleton);

  useEffect(() => {
    if (!registry) {
      const newRegistry = new PluginRegistryImpl();
      singleton = newRegistry;
      setRegistry(newRegistry);
    }
  }, [registry]);

  return registry || new PluginRegistryImpl();
}
